/* eslint-disable @wyz/module/no-relative-import */
import * as React from 'react';
import { IQuoteCartContext, IQuoteCartProviderProps } from '~/purchase/types';
import { useQuoteCart, useServiceQuoteCart } from '~/purchase/hooks';
import { useManageQuoteCart, useManageServiceQuoteCart } from '~/purchase/api';
import { QuoteCartContext } from './contexts';

export function QuoteCartProvider({
  children,
  ...rest
}: React.PropsWithChildren<IQuoteCartProviderProps>) {
  const regularQuoteActions = useQuoteCart();
  const serviceQuoteActions = useServiceQuoteCart();
  const manageQuoteCart = useManageQuoteCart({
    get: { enabled: rest.mode === 'QUOTE_CART' },
  });
  const manageServiceQuoteCart = useManageServiceQuoteCart({
    get: { enabled: rest.mode === 'QUOTE_SERVICE_CART' },
  });
  const actions: Record<
    IQuoteCartProviderProps['mode'],
    IQuoteCartContext['actions']
  > = React.useMemo(() => {
    return {
      QUOTE_CART: regularQuoteActions.actions,
      QUOTE_SERVICE_CART: serviceQuoteActions.actions,
      EDIT_QUOTE:
        rest.mode === 'EDIT_QUOTE' ? rest.actions : regularQuoteActions.actions,
    };
  }, [regularQuoteActions.actions, serviceQuoteActions.actions, rest]);

  const data: Record<
    IQuoteCartProviderProps['mode'],
    IQuoteCartContext['data']
  > = React.useMemo(() => {
    return {
      QUOTE_CART: {
        isSuccess: manageQuoteCart.get.isSuccess,
        isLoading: manageQuoteCart.get.isLoading,
        isProcessing: manageQuoteCart.get.isProcessing,
        services: manageQuoteCart.get.data?.data.services || [],
        vatPercentage: manageQuoteCart.get.data?.data.vatPercentage || 0,
        isCartEmpty: Boolean(manageQuoteCart.get.data?.data.items.length === 0),
        currency: manageQuoteCart.get.data?.data.groups.at(0)?.items.at(0)
          ?.currency,
        vehicleTypes:
          manageQuoteCart.get.data?.data.items.map(
            (item) => item.tyre.vehicleType,
          ) || [],
        defaultProducts:
          manageQuoteCart.get.data?.data.items.reduce(
            (products, item) => ({
              ...products,
              [item.id]: { quantity: item.quantity },
            }),
            {},
          ) ?? {},
        defaultRimType: manageQuoteCart.get.data?.data.rimType,
        totalTaxExcl: 0,
        totalTaxIncl: 0,
        totalVatPrice: 0,
        totalDiscount: 0,
      },
      QUOTE_SERVICE_CART: {
        isSuccess: manageServiceQuoteCart.get.isSuccess,
        isLoading: manageServiceQuoteCart.get.isLoading,
        isProcessing: manageServiceQuoteCart.get.isProcessing,
        services: manageServiceQuoteCart.get.data?.data.services || [],
        vatPercentage: manageServiceQuoteCart.get.data?.data.vatPercentage || 0,
        totalTaxIncl: manageServiceQuoteCart.get.data?.data.totalTaxIncl || 0,
        totalTaxExcl: manageServiceQuoteCart.get.data?.data.totalTaxExcl || 0,
        totalVatPrice: manageServiceQuoteCart.get.data?.data.totalVatPrice || 0,
        totalDiscount: manageServiceQuoteCart.get.data?.data.totalDiscount || 0,
        currency: 'EUR',
        isCartEmpty: Boolean(
          manageServiceQuoteCart.get.data?.data.services.length === 0,
        ),
        vehicleTypes: [],
        defaultProducts: {},
        defaultRimType: undefined,
      },
      EDIT_QUOTE:
        rest.mode === 'EDIT_QUOTE'
          ? rest.data
          : (undefined as unknown as IQuoteCartContext['data']),
    };
  }, [
    manageQuoteCart.get.data?.data.groups,
    manageQuoteCart.get.data?.data.items,
    manageQuoteCart.get.data?.data.rimType,
    manageQuoteCart.get.data?.data.services,
    manageQuoteCart.get.data?.data.vatPercentage,
    manageQuoteCart.get.isLoading,
    manageQuoteCart.get.isProcessing,
    manageQuoteCart.get.isSuccess,
    manageServiceQuoteCart.get.data?.data.services,
    manageServiceQuoteCart.get.data?.data.totalTaxExcl,
    manageServiceQuoteCart.get.data?.data.totalTaxIncl,
    manageServiceQuoteCart.get.data?.data.totalVatPrice,
    manageServiceQuoteCart.get.data?.data.totalDiscount,
    manageServiceQuoteCart.get.data?.data.vatPercentage,
    manageServiceQuoteCart.get.isLoading,
    manageServiceQuoteCart.get.isProcessing,
    manageServiceQuoteCart.get.isSuccess,
    rest,
  ]);
  const context: IQuoteCartContext = React.useMemo(
    () => ({
      mode: rest.mode,
      actions: actions[rest.mode],
      data: data[rest.mode],
    }),
    [actions, data, rest.mode],
  );
  return (
    <QuoteCartContext.Provider value={context}>
      {children}
    </QuoteCartContext.Provider>
  );
}

export const useQuoteCartContext = () => {
  const context = React.useContext(QuoteCartContext);
  if (context === undefined) {
    throw new Error(
      'useQuoteCartContext must be used within a QuoteCartContext',
    );
  }
  return context;
};
